import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Save, Settings2, X, ListChecks, AlertTriangle } from 'lucide-react'; // Using lucide-react

// UI Components (Shadcn)
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  CardFooter,
} from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { ScrollArea } from 'components/ui/scroll-area';
import { Separator } from 'components/ui/separator';

import LoadingSpinner from 'app/containers/OrderDetails/LoadingSpinner';
import SiteWrapper from 'app/containers/SiteWrapper';
import FormInputErrorStyled from 'app/components/styles/FormInputErrorStyled';

// Services & Types
import {
  useGetCarriersQuery,
  useGetCarrierConfigurationQuery,
  useUpdateCarrierConfigurationMutation,
} from 'services/kewloxApi';

interface ApiCarrier {
  id: number;
  name: string;
  // color & textColor are removed as we'll use a different list item style
  active?: boolean;
  // Add other properties if they exist on the API carrier object
}

const labelMap: Record<string, string> = {
  fuelFee: 'Supplément essence (en %)',
  kewloxFee: 'Marge Kewlox',
  meetingFee: 'Frais de rendez-vous',
  seasonTax: 'Taxe saisonnière (en %)',
  wrappingUpFee: "Frais d'emballage",
  urbanDeliveryFee: 'Taxe urbaine',
  knAdministrativeFee: 'Frais administratif du transporteur',
  manualHandlingFee: 'Supplément traitement manuel BPOST',
  annualIndex: 'Indexation annuelle (en %)',
};
const percentageFields = new Set(['fuelFee', 'seasonTax', 'annualIndex']);
const fallbackLabel = (camel: string): string => {
  return camel
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase());
};
type RawConfig = Record<string, any>;
type FormValues = Record<string, Record<string, string>>;

// Main Combined Page Component
const CarrierCombinedPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: routeCarrierId } = useParams<{ id?: string }>();

  const [selectedCarrierId, setSelectedCarrierId] = useState<number | null>(
    null,
  );
  const [jsonData, setJsonData] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'form' | 'json'>('form');
  const [activeGroup, setActiveGroup] = useState<string>('');

  // --- Data Fetching ---
  const {
    data: apiCarriers,
    isLoading: isLoadingCarriers,
    isError: isErrorCarriers,
    error: carriersError,
  } = useGetCarriersQuery();

  const {
    data: carrierConfiguration,
    isLoading: isLoadingCarrierConf,
    refetch: refetchCarrierConfiguration,
  } = useGetCarrierConfigurationQuery(selectedCarrierId!, {
    skip: !selectedCarrierId,
  });

  const [updateCarrierConfiguration, { isLoading: isUpdating }] =
    useUpdateCarrierConfigurationMutation();

  // --- Effects ---
  useEffect(() => {
    if (routeCarrierId) {
      const numericId = parseInt(routeCarrierId, 10);
      if (!isNaN(numericId) && apiCarriers?.find(c => c.id === numericId)) {
        setSelectedCarrierId(numericId);
      }
    }
  }, [routeCarrierId, apiCarriers, navigate]);

  useEffect(() => {
    if (selectedCarrierId && carrierConfiguration) {
      const raw = carrierConfiguration.data as RawConfig;
      setJsonData(JSON.stringify(raw, null, 2));
      const newIsNested = Object.values(raw).some(
        val => typeof val === 'object' && val !== null,
      );
      const newGroups = newIsNested ? Object.keys(raw) : ['global'];
      if (!activeGroup || !newGroups.includes(activeGroup)) {
        setActiveGroup(newGroups[0] || '');
      }
      reset(createInitialValues(raw, newIsNested, newGroups));
    } else {
      setJsonData('');
      reset({});
      setActiveGroup('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCarrierId, carrierConfiguration]); // `reset` is stable

  // --- Memoized Values ---
  const currentSelectedCarrier = useMemo(() => {
    return apiCarriers?.find(c => c.id === selectedCarrierId);
  }, [apiCarriers, selectedCarrierId]);

  const isNestedConfig = useMemo(() => {
    if (!carrierConfiguration) return false;
    const raw = carrierConfiguration.data as RawConfig;
    return Object.values(raw).some(
      val => typeof val === 'object' && val !== null,
    );
  }, [carrierConfiguration]);

  const configGroups: string[] = useMemo(() => {
    if (!carrierConfiguration) return [];
    const raw = carrierConfiguration.data as RawConfig;
    return isNestedConfig ? Object.keys(raw) : ['global'];
  }, [carrierConfiguration, isNestedConfig]);

  const validationSchema = useMemo<Yup.AnySchema>(() => {
    if (!carrierConfiguration) return Yup.object();
    const raw = carrierConfiguration.data as RawConfig;

    if (isNestedConfig) {
      const shapePerCountry: Record<string, Yup.AnySchema> = {};
      configGroups.forEach(country => {
        const countryData = (raw[country] as Record<string, any>) || {};
        const fieldKeys = Object.keys(countryData);
        const innerShape: Record<string, Yup.AnySchema> = {};
        fieldKeys.forEach(key => {
          innerShape[key] = Yup.number()
            .typeError(
              `${labelMap[key] ?? fallbackLabel(key)} doit être un nombre`,
            )
            .nullable();
        });
        shapePerCountry[country] = Yup.object().shape(innerShape);
      });
      return Yup.object().shape(shapePerCountry);
    } else {
      const fieldKeys = Object.keys(raw as Record<string, any>);
      const flatShape: Record<string, Yup.AnySchema> = {};
      fieldKeys.forEach(key => {
        flatShape[key] = Yup.number()
          .typeError(
            `${labelMap[key] ?? fallbackLabel(key)} doit être un nombre`,
          )
          .nullable();
      });
      return Yup.object().shape({
        global: Yup.object().shape(flatShape),
      });
    }
  }, [carrierConfiguration, configGroups, isNestedConfig]);

  const createInitialValues = (
    rawConfigData: RawConfig | undefined,
    isNested: boolean,
    groups: string[],
  ): FormValues => {
    if (!rawConfigData) return {};

    if (isNested) {
      const result: Record<string, Record<string, string>> = {};
      groups.forEach(country => {
        result[country] = {};
        const countryData =
          (rawConfigData[country] as Record<string, any>) || {};
        Object.entries(countryData).forEach(([key, val]) => {
          if (val === null || val === undefined) {
            result[country][key] = '';
          } else if (percentageFields.has(key)) {
            result[country][key] = (Number(val) * 100).toFixed(2);
          } else {
            result[country][key] = `${val}`;
          }
        });
      });
      return result;
    } else {
      const result: Record<string, Record<string, string>> = { global: {} };
      Object.entries(rawConfigData as Record<string, any>).forEach(
        ([key, val]) => {
          if (val === null || val === undefined) {
            result.global[key] = '';
          } else if (percentageFields.has(key)) {
            result.global[key] = (Number(val) * 100).toFixed(2);
          } else {
            result.global[key] = `${val}`;
          }
        },
      );
      return result;
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {},
    mode: 'onBlur',
  });

  // --- Event Handlers ---
  const handleSelectCarrier = (carrierId: number) => {
    setSelectedCarrierId(carrierId);
    setActiveTab('form'); // Reset to form tab on new selection
    navigate(`/carrier-management/${carrierId}`, { replace: true });
  };

  // This function is now less critical if selection is primarily through the list
  // but can be kept if a dedicated "close" button on the form is desired.
  const handleClearSelection = () => {
    setSelectedCarrierId(null);
    navigate(`/carrier-management`, { replace: true });
  };

  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setJsonData(text);
    try {
      const parsed = JSON.parse(text) as RawConfig;
      const newIsNested = Object.values(parsed).some(
        v => typeof v === 'object' && v !== null,
      );
      const newGroups = newIsNested ? Object.keys(parsed) : ['global'];

      const newDefaults = createInitialValues(parsed, newIsNested, newGroups);

      if (!newGroups.includes(activeGroup)) {
        setActiveGroup(newGroups[0] || '');
      }
      reset(newDefaults as FormValues);
    } catch {
      // invalid JSON: do nothing, user is still typing
    }
  };

  const onSubmitConfiguration: SubmitHandler<FormValues> = values => {
    if (!carrierConfiguration || !selectedCarrierId) return;
    const apiData: RawConfig = {};

    if (isNestedConfig) {
      (Object.keys(values) as string[]).forEach(country => {
        apiData[country] = {};
        const fieldsObj = (values as Record<string, Record<string, string>>)[
          country
        ];
        Object.entries(fieldsObj).forEach(([key, strVal]) => {
          const numVal = parseFloat(strVal);
          apiData[country][key] = isNaN(numVal)
            ? null
            : percentageFields.has(key)
            ? numVal / 100
            : numVal;
        });
      });
    } else {
      const flatObj = (values as Record<string, Record<string, string>>).global;
      Object.entries(flatObj).forEach(([key, strVal]) => {
        const numVal = parseFloat(strVal);
        apiData[key] = isNaN(numVal)
          ? null
          : percentageFields.has(key)
          ? numVal / 100
          : numVal;
      });
    }

    updateCarrierConfiguration({
      id: carrierConfiguration.id,
      data: apiData,
    })
      .unwrap()
      .then(() => {
        toast.success('Les données ont été mises à jour');
        refetchCarrierConfiguration();
      })
      .catch(() => {
        toast.error(`Les données n'ont pas pu être mises à jour`);
      });
  };

  const onConfigurationError = (formErrors: any) => {
    console.error('⛔ Validation configuration échouée:', formErrors);
    toast.warn('Veuillez corriger les erreurs dans le formulaire.');
  };

  // --- Render Methods ---

  const renderCarrierSelectionColumn = () => {
    return (
      <Card className="flex flex-col h-full">
        {' '}
        {/* Ensure Card takes full height of its container */}
        <CardHeader>
          <CardTitle>Transporteurs</CardTitle>
          <CardDescription>
            Sélectionnez un transporteur pour le configurer.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-grow p-0">
          {' '}
          {/* flex-grow and remove padding for ScrollArea */}
          <ScrollArea className="h-full px-6 py-4">
            {' '}
            {/* Adjust height as needed, add padding back here */}
            {isLoadingCarriers && (
              <div className="flex justify-center items-center py-8">
                <LoadingSpinner size={32} />
              </div>
            )}
            {isErrorCarriers && (
              <div className="text-red-500 text-center py-8 px-2">
                <AlertTriangle className="inline-block mr-2 h-5 w-5" />
                Erreur de chargement.
              </div>
            )}
            {apiCarriers && apiCarriers.length > 0 && (
              <div className="space-y-2">
                {(apiCarriers as ApiCarrier[]).map(carrier => (
                  <Button
                    key={carrier.id}
                    variant={
                      selectedCarrierId === carrier.id ? 'secondary' : 'ghost'
                    }
                    className={`w-full justify-start text-left h-auto py-3 ${
                      selectedCarrierId === carrier.id ? 'font-semibold' : ''
                    }`}
                    onClick={() => handleSelectCarrier(carrier.id)}
                  >
                    {carrier.name}
                  </Button>
                ))}
              </div>
            )}
            {apiCarriers && apiCarriers.length === 0 && !isLoadingCarriers && (
              <p className="text-gray-500 text-center py-8">
                Aucun transporteur trouvé.
              </p>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    );
  };

  const renderGroupForm = (groupKey: string) => {
    const rawConfData = carrierConfiguration?.data as RawConfig;
    if (!rawConfData)
      return <p>Aucune donnée de configuration pour ce groupe.</p>;

    let fieldKeys: string[] = [];
    if (isNestedConfig) {
      fieldKeys = Object.keys(
        (rawConfData[groupKey] as Record<string, any>) || {},
      );
    } else {
      fieldKeys = Object.keys(rawConfData as Record<string, any>);
    }

    if (fieldKeys.length === 0) {
      return <p>Aucun champ configurable pour {groupKey.toUpperCase()}.</p>;
    }

    const groupErrors: Record<string, any> = (errors as any)[groupKey] || {};
    const currentGroupValues =
      createInitialValues(rawConfData, isNestedConfig, configGroups)[
        groupKey
      ] || {};

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {fieldKeys.map(key => {
            const label = labelMap[key] ?? fallbackLabel(key);
            const inputName = `${groupKey}.${key}`; // Correct RHF name
            return (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={inputName}>{label}</Label>
                <Input
                  id={inputName}
                  {...register(inputName as any)}
                  className={
                    groupErrors[key]
                      ? 'border-red-500 focus-visible:ring-red-500'
                      : ''
                  }
                  type="text"
                  placeholder={
                    percentageFields.has(key)
                      ? 'ex: 10.5 (pour 10.5%)'
                      : 'ex: 25.50'
                  }
                  //defaultValue={currentGroupValues[key] || ''} // RHF handles defaultValues
                />
                {groupErrors[key] && (
                  <FormInputErrorStyled>
                    {groupErrors[key]?.message}
                  </FormInputErrorStyled>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderConfigurationColumn = () => {
    if (!selectedCarrierId) {
      return (
        <Card className="h-full flex flex-col items-center justify-center">
          <CardContent className="text-center">
            <ListChecks className="mx-auto h-16 w-16 text-gray-400 mb-6" />
            <p className="text-xl font-medium text-gray-700">
              Gestion des Configurations
            </p>
            <p className="text-gray-500 mt-2">
              Sélectionnez un transporteur dans la liste de gauche pour afficher
              et modifier sa configuration.
            </p>
          </CardContent>
        </Card>
      );
    }

    if (isLoadingCarrierConf && !carrierConfiguration) {
      return (
        <Card className="h-full flex items-center justify-center">
          <CardContent>
            <div className="flex items-center text-lg">
              <LoadingSpinner size={24} className="mr-3" />
              Chargement de la configuration...
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!carrierConfiguration && !isLoadingCarrierConf) {
      return (
        <Card className="h-full flex flex-col items-center justify-center">
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="h-6 w-6 text-orange-500 mr-2" />
              Information
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-orange-600">
              Aucune configuration trouvée pour{' '}
              {currentSelectedCarrier?.name || 'ce transporteur'}.
            </p>
            <Button
              onClick={() => refetchCarrierConfiguration()}
              variant="link"
              className="mt-2"
            >
              Réessayer le chargement
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearSelection}
              className="mt-4"
            >
              Choisir un autre transporteur
            </Button>
          </CardContent>
        </Card>
      );
    }

    if (!carrierConfiguration) return null; // Should be caught

    return (
      <Card className="flex flex-col h-full">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Configuration: {currentSelectedCarrier?.name}</CardTitle>
            <CardDescription>
              Modifiez les paramètres ou éditez le JSON brut.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClearSelection}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Fermer la configuration"
          >
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        <CardContent className="flex-grow overflow-y-auto">
          {' '}
          {/* Make content scrollable if it overflows */}
          {isUpdating && (
            <div className="italic my-2 p-2 text-sm bg-blue-50 border border-blue-200 text-blue-700 rounded-md flex items-center">
              <LoadingSpinner size={16} className="mr-2" />
              Mise à jour des prix en cours…
            </div>
          )}
          <form
            onSubmit={handleSubmit(onSubmitConfiguration, onConfigurationError)}
            className="mt-1" // Reduced top margin
          >
            <Tabs
              value={activeTab}
              onValueChange={v => setActiveTab(v as 'form' | 'json')}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="form">Formulaire Détaillé</TabsTrigger>
                <TabsTrigger value="json">Éditeur JSON</TabsTrigger>
              </TabsList>

              <TabsContent value="form">
                {configGroups.length > 0 ? (
                  isNestedConfig ? (
                    <Tabs
                      value={activeGroup}
                      onValueChange={val => setActiveGroup(val)}
                      orientation="vertical" // For a more modern feel with many groups
                      className="w-full"
                    >
                      <TabsList className="mr-4 whitespace-nowrap min-w-[120px]">
                        {configGroups.map(g => (
                          <TabsTrigger
                            key={g}
                            value={g}
                            className="w-full justify-start px-3 py-2"
                          >
                            {g.toUpperCase()}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {configGroups.map(g => (
                        <TabsContent
                          key={g}
                          value={g}
                          className="pl-4 border-l"
                        >
                          {' '}
                          {/* Add padding and border */}
                          {renderGroupForm(g)}
                        </TabsContent>
                      ))}
                    </Tabs>
                  ) : (
                    activeGroup && renderGroupForm(activeGroup)
                  )
                ) : (
                  <p className="text-gray-500 py-4 text-center">
                    Cette configuration est vide ou non modifiable via le
                    formulaire.
                  </p>
                )}
              </TabsContent>

              <TabsContent value="json">
                <div className="space-y-3">
                  <Label htmlFor="json-editor" className="font-medium">
                    Configuration JSON Brute
                  </Label>
                  <Textarea
                    id="json-editor"
                    value={jsonData}
                    onChange={handleJsonChange}
                    className="font-mono h-96 text-sm" // Adjust height
                    placeholder="Collez ou modifiez la configuration JSON ici..."
                  />
                  <p className="text-xs text-gray-500">
                    Les modifications ici impacteront le formulaire après une
                    validation JSON.
                  </p>
                </div>
              </TabsContent>
            </Tabs>
            {/* Moved save button to CardFooter for better positioning */}
          </form>
        </CardContent>
        <CardFooter className="border-t pt-6">
          <Button
            type="submit" // Will trigger the form submit from outside
            onClick={handleSubmit(onSubmitConfiguration, onConfigurationError)} // Also manually trigger if outside form
            disabled={
              isLoadingCarrierConf ||
              isUpdating ||
              Object.keys(errors).length > 0
            }
            className="w-full md:w-auto ml-auto"
          >
            {(isLoadingCarrierConf || isUpdating) && (
              <LoadingSpinner size={16} className="mr-2" />
            )}
            <Save className="h-4 w-4 mr-2" />
            Sauvegarder la Configuration
          </Button>
        </CardFooter>
      </Card>
    );
  };

  // --- Main Return ---
  return (
    <>
      <Helmet>
        <title>Gestion des Transporteurs</title>
        <meta
          name="description"
          content="Page de gestion et configuration des transporteurs."
        />
      </Helmet>

      <SiteWrapper
        title="Gestion des Transporteurs"
        titleClassName="text-2xl font-bold"
      >
        {/* This div will manage the two-column layout and their heights */}
        <div className="flex flex-col md:flex-row gap-6 flex-grow overflow-hidden p-1">
          {' '}
          {/* flex-grow and overflow-hidden are key */}
          {/* Left Column: Carrier Selector */}
          <div className="w-full md:w-[320px] lg:w-[360px] flex-shrink-0 h-full md:h-auto md:max-h-[calc(100vh-150px)]">
            {' '}
            {/* Adjust max-h based on your header/footer */}
            {/* 150px is an estimate for SiteWrapper titlebar and page paddings. Adjust it. */}
            {renderCarrierSelectionColumn()}
          </div>
          {/* Separator (Optional) - visible on md and up */}
          {/* <Separator orientation="vertical" className="hidden md:block h-auto" /> */}
          {/* Right Column: Configuration Form / Placeholder */}
          <div className="flex-grow h-full md:h-auto md:max-h-[calc(100vh-150px)] overflow-y-auto">
            {' '}
            {/* overflow-y-auto for this column if content exceeds max-h */}
            {renderConfigurationColumn()}
          </div>
        </div>
      </SiteWrapper>
    </>
  );
};

export default CarrierCombinedPage;
